import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { defaultPfpUrl } from "../utils";

const UserCard = ({ affiliation }) => {
  const { user, role } = affiliation;

  return (
    <Card className="max-w-full overflow-x-clip shadow-profile-sm transition-shadow hover:shadow-profile-md">
      <CardContent className="p-4">
        <div className="flex min-w-0 items-start gap-4">
          <div className="shrink-0">
            <img
              src={user.pfp_url || defaultPfpUrl}
              alt=""
              className="h-12 w-12 rounded-full object-cover ring-1 ring-ui-border"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = defaultPfpUrl;
              }}
            />
          </div>

          <div className="min-w-0 flex-1 overflow-hidden">
            <div className="mb-1 flex min-w-0 flex-wrap items-center gap-2">
              <Link
                to={`/profile/${user.id}`}
                className="break-words text-lg font-semibold text-accent transition-colors hover:text-text-accent-hover"
              >
                {user.first_name} {user.last_name}
              </Link>
              {role && (
                <Badge variant="outline" className="font-normal text-accent">
                  {role}
                </Badge>
              )}
            </div>

            {user.location && (
              <div className="mb-1 flex items-center text-text-secondary">
                <svg className="mr-1 h-4 w-4 shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                  <path
                    fillRule="evenodd"
                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm">{user.location}</span>
              </div>
            )}

            {user.mission_statement && (
              <p className="mb-1 text-sm italic text-text-secondary">
                &ldquo;{user.mission_statement}&rdquo;
              </p>
            )}

            {user.bio && (
              <p className="mb-1 line-clamp-2 text-sm text-text-secondary">{user.bio}</p>
            )}

            <div className="mt-1 flex min-w-0 flex-wrap gap-x-4 gap-y-1 text-xs text-text-muted">
              {user.email && (
                <div className="flex min-w-0 max-w-full items-center gap-1">
                  <svg className="h-3 w-3 shrink-0 text-text-secondary" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  <span className="min-w-0 truncate text-text-secondary">{user.email}</span>
                </div>
              )}
              {user.phone && (
                <div className="flex min-w-0 max-w-full items-center gap-1">
                  <svg className="h-3 w-3 shrink-0 text-text-secondary" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  <span className="min-w-0 truncate text-text-secondary">{user.phone}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const OrganizationCard = ({ organization }) => {
  return (
    <Card className="shadow-profile-sm transition-shadow hover:shadow-profile-md">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="shrink-0">
            <img
              src={organization.pfp_url || defaultPfpUrl}
              alt=""
              className="h-12 w-12 rounded-full object-cover ring-1 ring-ui-border"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = defaultPfpUrl;
              }}
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <Link
                to={`/organization/${organization.id}`}
                className="text-lg font-semibold text-accent transition-colors hover:text-text-accent-hover"
              >
                {organization.name}
              </Link>
              {organization.website && (
                <a
                  href={organization.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-text-muted transition-colors hover:text-text-secondary"
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                    <path
                      fillRule="evenodd"
                      d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z"
                      clipRule="evenodd"
                    />
                  </svg>
                </a>
              )}
            </div>

            {organization.address && (
              <div className="mb-1 flex items-center text-text-secondary">
                <svg className="mr-1 h-4 w-4 shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                  <path
                    fillRule="evenodd"
                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm">{organization.address}</span>
              </div>
            )}

            {organization.mission_statement && (
              <p className="mb-1 text-sm italic text-text-secondary">
                &ldquo;{organization.mission_statement}&rdquo;
              </p>
            )}

            {organization.bio && (
              <p className="mb-1 line-clamp-2 text-sm text-text-secondary">{organization.bio}</p>
            )}

            <div className="mt-1 flex flex-wrap gap-4 text-xs text-text-muted">
              {organization.email && (
                <div className="flex items-center gap-1">
                  <svg className="h-3 w-3 text-text-secondary" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  <span className="text-text-secondary">{organization.email}</span>
                </div>
              )}
              {organization.phone && (
                <div className="flex items-center gap-1">
                  <svg className="h-3 w-3 text-text-secondary" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  <span className="text-text-secondary">{organization.phone}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export { UserCard };
export default OrganizationCard;
